import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CLASS_SESSION_STATUS, UNLOCK_REQUEST_STATUS } from 'enums';
import {
  ClassAllowlistChangedEvent,
  ClassCandidateMember,
  ClassFocusSession,
  ClassInfo,
  ClassParticipantUpdatedEvent,
  ClassRosterUpdatePayload,
  ClassConcernCriteria,
  ClassSessionListItem,
  ClassSessionParticipant,
  ClassSessionReport,
  ClassStudentAttendance,
  ClassTasksUpdatedEvent,
  ClassUnlockRequest,
  SharedFocusMode
} from 'interfaces/classes.interface';
import { initialClassManagementState } from 'store/initial-states';
import {
  countJoinedParticipants,
  mergeParticipantPresence,
  patchLiveJoinedCountOnClasses
} from 'utils/class-management.util';
import { upsertLiveUnlockRequest } from 'utils/class-unlock-requests.util';

const classManagement = createSlice({
  name: 'classManagement',
  initialState: initialClassManagementState,
  reducers: {
    updateAreClassesLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.areClassesLoading = payload;
    },
    updateClasses: (state, { payload }: PayloadAction<ClassInfo[]>) => {
      state.classes = payload;
    },
    updateClassesInitialized: (state, { payload }: PayloadAction<boolean>) => {
      state.classesInitialized = payload;
    },
    updateSelectedClass: (
      state,
      { payload }: PayloadAction<ClassInfo | null>
    ) => {
      const isDifferentClass = payload?.id !== state.selectedClass?.id;
      state.selectedClass = payload;
      if (!payload || isDifferentClass) {
        state.liveSession = null;
        state.participants = [];
        state.unlockRequests = [];
        state.reportsBySessionId = {};
        state.reportLoadingSessionIds = [];
        state.allowlistTemplateSavedSessionId = null;
        state.classMembers = [];
        state.classAdminIds = [];
        state.classTeacherIds = [];
        state.classStudents = [];
        state.classTeachers = [];
        state.candidateMembers = [];
        state.areCandidateMembersRestricted = false;
        state.sessions = [];
        state.attendance = [];
        state.concernCriteria = null;
      }
    },
    updateIsCreatingClass: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingClass = payload;
    },
    updateIsRenamingClass: (state, { payload }: PayloadAction<boolean>) => {
      state.isRenamingClass = payload;
    },
    updateIsArchivingClass: (state, { payload }: PayloadAction<boolean>) => {
      state.isArchivingClass = payload;
    },
    updateLiveSession: (
      state,
      { payload }: PayloadAction<ClassFocusSession | null>
    ) => {
      state.liveSession = payload;
    },
    updateIsLiveSessionLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isLiveSessionLoading = payload;
    },
    updateIsStartingSession: (state, { payload }: PayloadAction<boolean>) => {
      state.isStartingSession = payload;
    },
    updateIsEndingSession: (state, { payload }: PayloadAction<boolean>) => {
      state.isEndingSession = payload;
    },
    updateJustEndedSessionId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.justEndedSessionId = payload;
    },
    updateIsAmendingAllowlist: (state, { payload }: PayloadAction<boolean>) => {
      state.isAmendingAllowlist = payload;
    },
    updateIsAmendingSessionTasks: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAmendingSessionTasks = payload;
    },
    updateSharedFocusModes: (
      state,
      { payload }: PayloadAction<SharedFocusMode[]>
    ) => {
      state.sharedFocusModes = payload;
    },
    updateIsSavingAllowlistToTemplate: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSavingAllowlistToTemplate = payload;
    },
    updateAllowlistTemplateSavedSessionId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.allowlistTemplateSavedSessionId = payload;
    },
    updateAreSharedFocusModesLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areSharedFocusModesLoading = payload;
    },
    updateParticipants: (
      state,
      { payload }: PayloadAction<ClassSessionParticipant[]>
    ) => {
      state.participants = payload;
      const liveSessionId = state.liveSession?.id;
      if (!liveSessionId) {
        return;
      }
      const joinedCount = countJoinedParticipants(payload);
      state.classes = patchLiveJoinedCountOnClasses(state.classes, {
        classSessionId: liveSessionId,
        joinedCount
      });
      if (state.selectedClass?.live_session?.id === liveSessionId) {
        state.selectedClass = {
          ...state.selectedClass,
          live_joined_count: joinedCount
        };
      }
    },
    updateAreParticipantsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areParticipantsLoading = payload;
    },
    updateUnlockRequests: (
      state,
      { payload }: PayloadAction<ClassUnlockRequest[]>
    ) => {
      state.unlockRequests = payload;
    },
    updateAreUnlockRequestsLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areUnlockRequestsLoading = payload;
    },
    updateResolvingUnlockRequestId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.resolvingUnlockRequestId = payload;
    },
    updateResolvingUnlockStudentId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.resolvingUnlockStudentId = payload;
    },
    updateReleasingStudentId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.releasingStudentId = payload;
    },
    updateReportForSession: (
      state,
      {
        payload
      }: PayloadAction<{ sessionId: string; report: ClassSessionReport | null }>
    ) => {
      if (payload.report) {
        state.reportsBySessionId[payload.sessionId] = payload.report;
      } else {
        delete state.reportsBySessionId[payload.sessionId];
      }
    },
    updateReportLoadingForSession: (
      state,
      { payload }: PayloadAction<{ sessionId: string; isLoading: boolean }>
    ) => {
      state.reportLoadingSessionIds = payload.isLoading
        ? [...new Set([...state.reportLoadingSessionIds, payload.sessionId])]
        : state.reportLoadingSessionIds.filter(
            (id) => id !== payload.sessionId
          );
    },
    updateClassMembers: (
      state,
      { payload }: PayloadAction<ClassRosterUpdatePayload>
    ) => {
      state.classMembers = payload.members ?? [];
      state.classAdminIds = payload.admins ?? [];
      state.classTeacherIds = payload.teachers ?? [];
      state.classStudents = payload.classStudents ?? [];
      state.classTeachers = payload.classTeachers ?? [];
    },
    updateAreClassMembersLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areClassMembersLoading = payload;
    },
    updateRemovingMemberId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.removingMemberId = payload;
    },
    updateCandidateMembers: (
      state,
      { payload }: PayloadAction<ClassCandidateMember[]>
    ) => {
      state.candidateMembers = payload;
    },
    updateAreCandidateMembersLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areCandidateMembersLoading = payload;
    },
    updateAreCandidateMembersRestricted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areCandidateMembersRestricted = payload;
    },
    updateIsAddingMembers: (state, { payload }: PayloadAction<boolean>) => {
      state.isAddingMembers = payload;
    },
    updateSessions: (
      state,
      { payload }: PayloadAction<ClassSessionListItem[]>
    ) => {
      state.sessions = payload;
    },
    updateAreSessionsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.areSessionsLoading = payload;
    },
    updateAttendance: (
      state,
      { payload }: PayloadAction<ClassStudentAttendance[]>
    ) => {
      state.attendance = payload;
    },
    updateAreAttendanceLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.areAttendanceLoading = payload;
    },
    setConcernCriteria: (
      state,
      { payload }: PayloadAction<ClassConcernCriteria | null>
    ) => {
      state.concernCriteria = payload;
    },
    updateShowStartSessionModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showStartSessionModal = payload;
    },
    updateCanStartSession: (state, { payload }: PayloadAction<boolean>) => {
      state.canStartSession = payload;
    },
    updateShowStudentsModal: (state, { payload }: PayloadAction<boolean>) => {
      state.showStudentsModal = payload;
    },
    updateShowSessionHistoryModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showSessionHistoryModal = payload;
    },
    updateShowClassReportsModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showClassReportsModal = payload;
    },
    /** Pusher `class-participant-updated` on `presence-class-<teamId>`. */
    applyParticipantUpdated: (
      state,
      { payload }: PayloadAction<ClassParticipantUpdatedEvent>
    ) => {
      if (state.liveSession?.id !== payload.class_session_id) return;
      state.participants = mergeParticipantPresence(
        state.participants,
        payload
      );
      const joinedCount = countJoinedParticipants(state.participants);
      state.classes = patchLiveJoinedCountOnClasses(state.classes, {
        classSessionId: payload.class_session_id,
        joinedCount
      });
      if (state.selectedClass?.live_session?.id === payload.class_session_id) {
        state.selectedClass = {
          ...state.selectedClass,
          live_joined_count: joinedCount
        };
      }
    },
    /** Pusher `class-allowlist-changed`; applied only if the version is newer. */
    applyAllowlistChanged: (
      state,
      { payload }: PayloadAction<ClassAllowlistChangedEvent>
    ) => {
      if (
        state.liveSession?.id !== payload.class_session_id ||
        payload.allowlist_version <= state.liveSession.allowlist_version
      ) {
        return;
      }
      state.liveSession = {
        ...state.liveSession,
        allowed_apps: payload.allowed_apps,
        allowed_urls: payload.allowed_urls,
        allowlist_version: payload.allowlist_version,
        ai_blocking_enabled:
          payload.ai_blocking_enabled ?? state.liveSession.ai_blocking_enabled,
        ai_contest_enabled:
          payload.ai_contest_enabled ?? state.liveSession.ai_contest_enabled
      };
    },
    /** Pusher `class-session-tasks-updated`; applied only if the version is newer. */
    applyTasksUpdated: (
      state,
      { payload }: PayloadAction<ClassTasksUpdatedEvent>
    ) => {
      if (
        state.liveSession?.id !== payload.class_session_id ||
        payload.tasks_version <= (state.liveSession.tasks_version ?? 1)
      ) {
        return;
      }
      state.liveSession = {
        ...state.liveSession,
        tasks: payload.tasks,
        tasks_version: payload.tasks_version
      };
    },
    /** Pusher `class-unlock-requested` (teacher alert). */
    applyUnlockRequested: (
      state,
      { payload }: PayloadAction<ClassUnlockRequest>
    ) => {
      if (state.liveSession?.id !== payload.class_session_id) return;
      state.unlockRequests = upsertLiveUnlockRequest(
        state.unlockRequests,
        payload
      );
    },
    /** Pusher `class-session-ended`. `ended_at` comes from the event handler
     * so the reducer stays pure. */
    applySessionEnded: (
      state,
      { payload }: PayloadAction<{ class_session_id: string; ended_at: string }>
    ) => {
      if (state.liveSession?.id !== payload.class_session_id) return;
      if (state.liveSession.status === CLASS_SESSION_STATUS.LIVE) {
        state.justEndedSessionId = payload.class_session_id;
      }
      state.liveSession = {
        ...state.liveSession,
        status: CLASS_SESSION_STATUS.ENDED,
        ended_at: state.liveSession.ended_at ?? payload.ended_at
      };
      state.unlockRequests = state.unlockRequests.filter(
        (request) => request.status !== UNLOCK_REQUEST_STATUS.PENDING
      );
    }
  }
});

export const {
  updateAreClassesLoading,
  updateClasses,
  updateClassesInitialized,
  updateSelectedClass,
  updateIsCreatingClass,
  updateIsRenamingClass,
  updateIsArchivingClass,
  updateLiveSession,
  updateIsLiveSessionLoading,
  updateIsStartingSession,
  updateIsEndingSession,
  updateIsAmendingAllowlist,
  updateIsAmendingSessionTasks,
  updateParticipants,
  updateAreParticipantsLoading,
  updateUnlockRequests,
  updateAreUnlockRequestsLoading,
  updateResolvingUnlockRequestId,
  updateResolvingUnlockStudentId,
  updateReleasingStudentId,
  updateReportForSession,
  updateReportLoadingForSession,
  updateClassMembers,
  updateAreClassMembersLoading,
  updateRemovingMemberId,
  updateCandidateMembers,
  updateAreCandidateMembersLoading,
  updateAreCandidateMembersRestricted,
  updateIsAddingMembers,
  updateSessions,
  updateAreSessionsLoading,
  updateAttendance,
  updateAreAttendanceLoading,
  setConcernCriteria,
  updateShowStartSessionModal,
  updateCanStartSession,
  updateJustEndedSessionId,
  updateShowStudentsModal,
  updateShowSessionHistoryModal,
  updateShowClassReportsModal,
  updateSharedFocusModes,
  updateAreSharedFocusModesLoading,
  updateIsSavingAllowlistToTemplate,
  updateAllowlistTemplateSavedSessionId,
  applyParticipantUpdated,
  applyAllowlistChanged,
  applyTasksUpdated,
  applyUnlockRequested,
  applySessionEnded
} = classManagement.actions;

export default classManagement.reducer;
